import React from 'react'
import { Button, Col, Form, Input, Row, Space } from 'antd'
import { v4 as uuidv4 } from 'uuid'
import { actLoadSecured, actSaveSecured } from '../../../stores/secured/actions'
import { observer } from 'mobx-react-lite'
import LstTypeInputBase from '../../../components/Form/LstTypeInputBase'
import { Secured } from '../../../cls/modules/secured'
import DynamicFormComponent from '../../../components/Form/DynamicFormComponent'
import { toJS } from 'mobx'
import { useStores } from '../../../cls/stores/MobXProvider'
import {
	GeneralContentWrapper,
	GeneralTourTitle
} from '../../../components/Shared/GeneralComponents'
import { getTourArrayByKeys } from '../../../stores/tour/config'
import { useSmartTranslation } from '../../../cls/TranslationContext'

const SecuredSearch = () => {
	const { t } = useSmartTranslation()

	const [form] = Form.useForm()
	const { tourStore } = useStores()
	const hasSecuredId = Form.useWatch('securedId', form) || ''
	const hasSearchKey = Form.useWatch('search_key', form) || ''

	/**
	 * Handles form submission for searching secured form data
	 *
	 * @param {Object} vals - The form values used in searching: {search_name, search_key}.
	 */
	const _doSearch = (vals) => {
		if (vals !== null) {
			actLoadSecured(vals.search_name, vals.search_key)
				.then((res) => {
					if (res.status === 'success') {
						const data = res.data
						// Populate the form with the retrieved secured data
						form.setFieldsValue({
							securedId: data.secured_id,
							name: data.name,
							full: JSON.parse(data.full),
							key: hasSearchKey
						})
					} else if (res.status === 'error') {
						console.error('SecuredSearch Error:', res)
					}
				})
				.catch((err) => {
					console.log('SecuredSearch Error:', err)
				})
		}
	}

	/**
	 * Handles form submission for saving secured form data
	 *
	 * @param {Object} vals - The form values.
	 */
	const _doSave = async (vals) => {
		if (vals !== null) {
			// Send plaintext data and key to the backend
			actSaveSecured(vals.securedId, vals.name, toJS(vals.full), vals.key)
				.then((res) => {
					console.log('SecuredSearch: Save response', res)
				})
				.catch((err) => {
					console.error('SecuredSearch Save Error:', err)
				})
		}
	}

	const securedList = getTourArrayByKeys('securedList', ['all'])

	// startTour
	/**
	 * Initiates the secured list tour by loading a predefined tour configuration
	 * and invoking the tour store to begin the walkthrough.
	 */
	const startTour = () => {
		tourStore.startTour('RequestLst-tour', [...securedList])
	}

	return (
		<GeneralContentWrapper>
			<GeneralTourTitle
				titleProps={{ text: t('single.secured.label') }}
				buttonProps={{
					onClick: startTour
				}}
			/>

			<Form form={form} name="secured-search" layout="vertical">
				{/* Hidden field for securedId */}
				<Form.Item name="securedId" hidden>
					<Input />
				</Form.Item>

				{hasSecuredId === '' && (
					<Row align="middle" gutter={[10, 10]}>
						<Col>
							<div id="target-search-name">
								<Form.Item
									label={t('single.search.label')}
									tooltip={t('single.name-tooltip')}
									name="search_name"
									rules={[
										{
											required: true,
											message: t(
												'single.secured.exact-name'
											)
										}
									]}
								>
									<Input
										placeholder={t('single.search.label')}
									/>
								</Form.Item>
							</div>
						</Col>
						<Col>
							<div id="target-search-key">
								<Form.Item
									label={t('single.key')}
									tooltip={t(
										'single.secured.enter-key-related'
									)}
									name="search_key"
									rules={[
										{
											required: true,
											message: t(
												'single.secured.exact-key'
											)
										}
									]}
								>
									<Input placeholder={t('single.key')} />
								</Form.Item>
							</div>
						</Col>
						<Col flex="auto">
							<Button
								id="target-search-btn"
								type="primary"
								onClick={() => {
									const search_name =
										form.getFieldValue('search_name')
									const search_key =
										form.getFieldValue('search_key')
									// Make sure the form is valid
									form.validateFields([
										'search_name',
										'search_key'
									]).then(() => {
										_doSearch({
											search_name,
											search_key
										})
									})
								}}
							>
								{t('single.search.label')}
							</Button>
						</Col>
						<Col flex="auto" align="end">
							<Button
								id="target-secured-add-new"
								onClick={() => {
									form.resetFields()
									form.setFieldsValue({
										securedId: uuidv4()
									})
								}}
							>
								{t('__add%single.new')}
							</Button>
						</Col>
					</Row>
				)}

				{hasSecuredId !== '' && (
					<Space
						direction="vertical"
						style={{ width: '100%', display: 'block', padding: 20 }}
					>
						<Space direction="horizontal">
							<Form.Item
								label={t('single.name')}
								name="name"
								tooltip={t('single.item-name.label')}
								rules={[
									{
										required: true,
										message: t('single.secured.exact-name')
									}
								]}
							>
								<Input />
							</Form.Item>
							<Form.Item
								label={t('single.key')}
								name="key"
								tooltip={t('single.secured.encryption-key')}
								rules={[
									{
										required: true,
										message: t('single.secured.enter-key')
									}
								]}
							>
								<Input />
							</Form.Item>
						</Space>
						<Form.Item
							label={t('single.item-label')}
							name="full"
							tooltip={t('single.item-label-data')}
							rules={[
								{
									required: true,
									message: t('single.item-label-data-msg')
								}
							]}
						>
							<LstTypeInputBase
								displayDescription
								noProgress
								UseLstClass={Secured}
								phaseTwoLimited
								additionalEditSection={(viewOnly) => {
									return (
										<Form.Item
											noStyle
											disabled={viewOnly}
											name="data"
											wrapperCol={{
												span: 24
											}}
										>
											<DynamicFormComponent
												viewOnly={viewOnly}
											/>
										</Form.Item>
									)
								}}
							/>
						</Form.Item>
						<Button
							type="primary"
							onClick={() => _doSave(form.getFieldsValue())}
						>
							{t('single.save')}
						</Button>
					</Space>
				)}
			</Form>
		</GeneralContentWrapper>
	)
}

export default observer(SecuredSearch)
